import { combineEpics, ofType } from 'redux-observable';
import { switchMap, catchError, mergeMap, map } from 'rxjs/operators';
import { normalize } from 'normalizr';
import { fetchAllSpacesForAllFolderTrees } from 'spaces/store/actions';
import api from 'api/index';
import { handleError } from 'base/errors/store/actions';
import * as actionTypes from './actionTypes';
import {
  fetchAclObjectIdentities,
  fetchAclObjectIdentitiesSuccess,
  fetchPermissionsBySidSuccess,
  deletePermissionSuccess,
  fetchAclSidsSuccess,
  fetchAllAclEntityIdToOidMapSuccess,
} from './actions';
import { permissionsMapSchema, aclObjectIdentitiesSchema, aclSidsSchema } from '../../../store/schema';
import { entitiesFetchSuccess } from '../../../store/entities/actions';

const fetchAclObjectIdentitiesEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_ACL_OBJECT_IDENTITIES),
    switchMap(() =>
      api.permissions.fetchAllAclObjectIdentities().pipe(
        map(response => normalize(response.body, aclObjectIdentitiesSchema)),
        mergeMap(({ entities, result }) => [
          entitiesFetchSuccess(entities),
          fetchAclObjectIdentitiesSuccess(entities, result),
        ]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchAclSids = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_ACL_SIDS),
    switchMap(() =>
      api.permissions.fetchAllAclSids().pipe(
        map(response => normalize(response.body, aclSidsSchema)),
        mergeMap(({ entities, result }) => [entitiesFetchSuccess(entities), fetchAclSidsSuccess(entities, result)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

function convertKeysToNumbers(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const numKey = Number.isNaN(Number(key)) ? key : Number(key);
      acc[numKey] = convertKeysToNumbers(value); // Recursive call for nested objects
      return acc;
    },
    Array.isArray(obj) ? [] : {},
  );
}

const fetchAllAclEntityIdToOidMap = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_ACL_SIDS),
    switchMap(() =>
      api.permissions.fetchAllAclEntityIdToOidIndex().pipe(
        mergeMap(response => {
          const index = convertKeysToNumbers(response.body);
          return [fetchAllAclEntityIdToOidMapSuccess(index)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const fetchPermissions = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_PERMISSIONS),
    switchMap(action =>
      api.permissions.fetchPermissionsBySid(action.sid).pipe(
        map(response => normalize(response.body, permissionsMapSchema)),
        mergeMap(({ entities, result }) => [fetchPermissionsBySidSuccess(entities, result, action.sid)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const updatePermission = action$ =>
  action$.pipe(
    ofType(actionTypes.UPDATE),
    mergeMap(action => {
      const permission = { ...action.permission };
      return api.permissions.update(permission).pipe(
        map(response => normalize(response.body, permissionsMapSchema)),
        mergeMap(({ entities, result }) => [fetchPermissionsBySidSuccess(entities, result)]),
        catchError(error => [handleError(error)]),
      );
    }),
  );

const deletePermission = action$ =>
  action$.pipe(
    ofType(actionTypes.DELETE),
    mergeMap(action => {
      const { objectIdentityId, sid } = action.permission;
      return api.permissions.delete(objectIdentityId, sid).pipe(
        mergeMap(() => [deletePermissionSuccess(action.permission)]),
        catchError(error => [handleError(error)]),
      );
    }),
  );

const fetchPermissionTreeSpaces = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_PERMISSION_TREE),
    switchMap(() => [fetchAllSpacesForAllFolderTrees(), fetchAclObjectIdentities()]),
  );

export default combineEpics(
  fetchAclObjectIdentitiesEpic,
  fetchAclSids,
  fetchAllAclEntityIdToOidMap,
  fetchPermissions,
  updatePermission,
  deletePermission,
  fetchPermissionTreeSpaces,
);
